import { useState, useRef, useEffect } from 'react';
import { formatDateLocal } from 'shared_items/formatConventions';
import { arrayMove } from '@dnd-kit/sortable';

// ##### Custom hook for todo edit mode management
export const useTodoEdit = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isClosingMenu, setIsClosingMenu] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  
  // ##### Refs for click outside detection
  const cardRef = useRef(null);
  const menuRef = useRef(null);
  const buttonMenuRef = useRef(null); // Ref for the three dots button (for portal positioning)
  const titleInputRef = useRef(null); // Ref for title input (for auto-focus)

  // ##### Keyboard Event Handler - ESC key to exit edit mode
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isEditMode) {
        setIsEditMode(false);
      }
      if (event.key === 'Escape' && isEditingTitle) {
        handleTitleCancel();
      }
    };

    if (isEditMode || isEditingTitle) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditMode, isEditingTitle]);

  // ##### Click Outside Handler - Exit edit mode when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isEditMode && cardRef.current && !cardRef.current.contains(event.target)) {
        setIsEditMode(false);
      }
    };

    if (isEditMode) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditMode]);

  // ##### Toggle edit mode
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // ##### Handle add item
  const handleAddItem = (onAddItem) => {
    if (newItem.trim()) {
      onAddItem(newItem.trim());
      setNewItem('');
    }
  };

  // ##### Handle drag start
  const handleDragStart = (event) => {
    console.log('🎯 Drag started:', event.active.id);
    setActiveId(event.active.id);
  };

  // ##### Handle drag end
  const handleDragEnd = (event, taskItems, onReorderItems) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over?.id) {
      // Handle regular item reordering
      const oldIndex = taskItems.findIndex(i => i.id === active.id);
      const newIndex = taskItems.findIndex(i => i.id === over.id);
      onReorderItems && onReorderItems(arrayMove(taskItems, oldIndex, newIndex));
    }
  };

  // ##### Handle menu toggle
  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setIsClosingMenu(false);
    setShowMenu(!showMenu);
  };

  // ##### Handle close menu with flag to prevent other actions
  const handleCloseMenu = () => {
    setIsClosingMenu(true);
    setShowMenu(false);
    // Reset flag after a short delay to allow event processing
    setTimeout(() => setIsClosingMenu(false), 100);
  };

  // ##### Handle delete click
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  // ##### Handle confirm delete
  const handleConfirmDelete = (onDeleteTask) => {
    setShowDeleteModal(false);
    onDeleteTask && onDeleteTask();
  };

  // ##### Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  // ##### Handle edit title click
  const handleEditTitleClick = (e, currentTitle) => {
    e.stopPropagation();
    setShowMenu(false);
    setEditingTitleValue(currentTitle);
    setIsEditingTitle(true);
  };

  // ##### Handle title change
  const handleTitleChange = (value) => {
    setEditingTitleValue(value);
  };

  // ##### Handle title save
  const handleTitleSave = async (onUpdateTodo, taskId) => {
    if (!editingTitleValue.trim()) {
      return null; // Don't save empty title
    }
    
    setIsSavingTitle(true);
    try {
      // Update title and date (date set to current date)
      const currentDate = formatDateLocal(new Date());
      const updatedTodo = await onUpdateTodo(taskId, { 
        title: editingTitleValue.trim(),
        date: currentDate
      });
      setIsEditingTitle(false);
      setEditingTitleValue('');
      return updatedTodo; // Return updated todo to update parent state
    } catch (error) {
      console.error('Error saving title:', error);
      // Keep editing mode open on error
      return null;
    } finally {
      setIsSavingTitle(false);
    }
  };

  // ##### Handle title cancel
  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditingTitleValue('');
  };

  // ##### Auto-focus title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  return {
    // State
    isEditMode,
    newItem,
    setNewItem,
    activeId,
    showDeleteModal,
    showMenu,
    setShowMenu,
    isClosingMenu,
    isEditingTitle,
    editingTitleValue,
    isSavingTitle,
    cardRef,
    menuRef,
    buttonMenuRef, // Export buttonMenuRef for portal positioning
    titleInputRef, // Export titleInputRef for auto-focus
    
    // Actions
    toggleEditMode,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
    handleMenuToggle,
    handleCloseMenu,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleEditTitleClick,
    handleTitleChange,
    handleTitleSave,
    handleTitleCancel
  };
};
